import cx from 'classnames';
import * as _ from 'lodash';
import { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import type { VoidFunctionComponent } from 'react';
import type { InjectedRouter } from 'react-router';

import { BINARY_DECOMPOSITION } from '../../../../shared/featureFlags/flagNames';
import { BinaryFetcherName } from '../../../../shared/fetchers';
import Locator from '../../../../shared/Locator';
import { ProjectSource } from '../../../../typings/enums';
import { useDresser } from '../../../contexts/DrawerContext';
import { window } from '../../../globals';
import { useIsFeatureEnabled } from '../../../helpers/features';
import { useOnUnmount } from '../../../hooks';
import { useReleaseGroupAllProjects } from '../../../hooks/api/releaseGroups';
import * as DependenciesV2Store from '../../../redux/dependenciesV2';
import PaginationFooter from '../../generic/Pagination/PaginationFooter';
import { SidebarLayout } from '../../layouts';
import ModalEditDependency from '../../modals/ModalEditDependency';
import ModalManagePackageLabels from '../../modals/ModalManagePackageLabels';
import type { ProjectGroup, ProjectGroupRelease } from '../../../../shared/model-typings';
import type { IAsync, Project, RouterProps } from '../../../types';

import DependenciesTable from './DependenciesTable';
import ProjectDependencyDrawer from './DependencyDrawer/ProjectDependencyDrawer';
import ReleaseGroupDependencyDrawer from './DependencyDrawer/ReleaseGroupDependencyDrawer';
import DependencyFilters from './filters';
import DependenciesHeader from './header';
import useDependencyModals from './hooks/useDependencyModals';
import useFetchDependencyAction from './hooks/useFetchDependencyAction';
import { useSyncStateAndURLParams } from './hooks/useSyncStateAndURLParams';
import { AddDependency, BinaryMatchDetailsModal, DeleteDependency, StreamBuild, ViewRelationships } from './modals';

import styles from './dependencies.module.less';

const countOptions = [
  25,
  50,
  75,
  100,
];

export type DependenciesV2Props = {
  router: InjectedRouter;
  project?: IAsync<Project>;
  projectGroup?: ProjectGroup;
  projectGroupRelease?: ProjectGroupRelease;
  onProjectChange?: () => void;
} & RouterProps;

const DependenciesV2: VoidFunctionComponent<DependenciesV2Props> = ({
  router,
  location,
  params,
  project,
  projectGroup,
  projectGroupRelease,
  onProjectChange = _.noop,
}) => {
  const dispatch = useDispatch();
  const { popDrawer, replaceDrawer } = useDresser();
  useSyncStateAndURLParams({ router, location, params });
  const modals = useDependencyModals();

  const { project_id: projectId, revision_id: revisionId } = params;
  const scope = useSelector(DependenciesV2Store.selectors.getScope);
  const isBinaryDecompEnabled = useIsFeatureEnabled(BINARY_DECOMPOSITION);

  // optional projectRevision used by the EditDependency modal
  const projectRevision = scope && scope.scope === 'project' ? scope.revision : undefined;
  const releaseGroupId = scope && scope.scope === 'releaseGroup' ? scope.projectGroupId : -1;

  const isContainerProject = (project && project?.data?.type === ProjectSource.Container) ?? false;
  const isBinaryProject = (project && project?.data?.type === ProjectSource.Binary) ?? false;

  const isReleaseGroupScope = useSelector(DependenciesV2Store.selectors.getIsReleaseGroupScope);
  const {
    data: releaseProjects,
  } = useReleaseGroupAllProjects(releaseGroupId, isBinaryDecompEnabled && releaseGroupId > 0);
  const isReleaseGroupWithBinaryProject = (
    isReleaseGroupScope &&
    releaseProjects?.some(releaseProject => {
      return Locator.parse(releaseProject).fetcher === BinaryFetcherName;
    })) ?? false;

  const page = useSelector(DependenciesV2Store.selectors.getPage);
  const count = useSelector(DependenciesV2Store.selectors.getCount);
  const totalItems = useSelector(DependenciesV2Store.selectors.getTotalDependencies);

  const setPage = useFetchDependencyAction(DependenciesV2Store.actions.setPage);
  const setCount = useFetchDependencyAction(DependenciesV2Store.actions.setCount);

  const [selectedDependencyRevisionLocator, setSelectedDependencyRevisionLocator] = useState<string | null>(null);

  const onClose = useCallback(() => {
    setSelectedDependencyRevisionLocator(null);
  }, [setSelectedDependencyRevisionLocator]);

  const onIgnoreProjectDependency = useCallback((dependencyRevisionLocator: string) => {
    // Close the drawer and reset the selected dependency revision locator
    setSelectedDependencyRevisionLocator(null);
    // Remove the dependency from the watch list to prevent unnecessary builds
    dispatch(DependenciesV2Store.actions.removeRevisionFromBuildWatch(dependencyRevisionLocator));
    // Trigger a project reload to refresh the dependencies on the page
    onProjectChange();
  }, [setSelectedDependencyRevisionLocator, dispatch, onProjectChange]);

  useEffect(() => {
    if (selectedDependencyRevisionLocator) {
      if (scope && scope.scope === 'releaseGroup' && selectedDependencyRevisionLocator) {
        replaceDrawer(
          <ReleaseGroupDependencyDrawer
            onClose={onClose}
            releaseGroupId={scope.projectGroupId}
            releaseGroupReleaseId={scope.projectGroupReleaseId}
            dependencyRevisionLocator={selectedDependencyRevisionLocator}
            scope={{
              type: 'releaseGroup',
              releaseGroupId: scope.projectGroupId,
              releaseId: scope.projectGroupReleaseId,
            }}
          />,
          { onClose, shouldSkipClickOutside: true },
        );
      } else if (scope && scope.scope === 'project' && selectedDependencyRevisionLocator) {
        replaceDrawer(
          <ProjectDependencyDrawer
            onClose={onClose}
            projectRevisionLocator={scope.revision}
            dependencyRevisionLocator={selectedDependencyRevisionLocator}
            onDeleteDependency={() => onIgnoreProjectDependency(selectedDependencyRevisionLocator)}
            scope={{
              type: 'project',
              projectLocator: Locator.parsePackageString(scope.revision),
              revisionLocator: scope.revision,
            }}
          />,
          { onClose, shouldSkipClickOutside: true },
        );
      }
    }
  }, [
    selectedDependencyRevisionLocator,
    scope,
    replaceDrawer,
    onIgnoreProjectDependency,
    popDrawer,
    onClose,
  ]);

  // If the current user is authenticated, check if they belong to the current scope target's org.
  if (window.user) {
    // Check if the user is logged in and belongs to the project's org.
    if (project && window.user.organizationId === project.data?.organizationId) {
      dispatch(DependenciesV2Store.actions.setIsOrgMember(true));
    }
    // Check if the user is logged in and belongs to the project group's org.
    if (projectGroup && window.user.organizationId === projectGroup.organizationId) {
      dispatch(DependenciesV2Store.actions.setIsOrgMember(true));
    }
  }

  useEffect(() => {
    if (projectGroup && projectGroupRelease) {
      dispatch(DependenciesV2Store.actions.setScope({
        scope: 'releaseGroup',
        projectGroupId: projectGroup.id,
        projectGroupReleaseId: projectGroupRelease.id,
      }));
    } else if (projectId && revisionId) {
      const revision = `${projectId}$${revisionId}`;
      dispatch(DependenciesV2Store.actions.setScope({ scope: 'project', revision }));
    }
  }, [dispatch, projectId, revisionId, projectGroup, projectGroupRelease]);

  useOnUnmount(() => {
    dispatch(DependenciesV2Store.actions.resetState());
  });

  return (
    <div className={cx('container', 'container-responsive', styles.DependenciesContainer)}>
      <SidebarLayout>
        <SidebarLayout.Header>
          <DependenciesHeader depModals={modals} />
        </SidebarLayout.Header>
        <SidebarLayout.Main>
          <DependenciesTable
            modals={modals}
            setSelectedDependencyRevisionLocator={setSelectedDependencyRevisionLocator}
          />
          <PaginationFooter
            page={page}
            count={count}
            totalItems={totalItems}
            setPage={setPage}
            setCount={setCount}
            countOptions={countOptions}
          />
        </SidebarLayout.Main>
        <SidebarLayout.Sidebar>
          <DependencyFilters
            shouldDisplayConfidenceFilter={isBinaryProject || isReleaseGroupWithBinaryProject}
            isContainerProject={isContainerProject}
            isReleaseGroupScope={isReleaseGroupScope}
          />
        </SidebarLayout.Sidebar>
      </SidebarLayout>
      <StreamBuild modals={modals} />
      {/* TODO: Remove this once the License Concluded Dependency Drawer feature is fully launched (CORE-5643) */}
      <ModalEditDependency
        show={modals.editDependencyModal.isOpen}
        onHide={modals.editDependencyModal.onClose}
        onChange={onProjectChange}
        revisionLocator={modals.editDependencyModal.context.revision}
        parentRevisionLocator={projectRevision}
      />
      {/* The following modals only apply to project dependencies. */}
      {scope && scope.scope === 'project' && (
        <>
          <ViewRelationships projectRevision={scope.revision} modals={modals} />
          <AddDependency projectRevision={scope.revision} modals={modals} onProjectChange={onProjectChange} />
          <DeleteDependency projectRevision={scope.revision} modals={modals} onProjectChange={onProjectChange} />
          <ModalManagePackageLabels
            show={modals.managePackageLabelsModal.isOpen}
            onHide={modals.managePackageLabelsModal.onClose}
            context={modals.managePackageLabelsModal.context}
            parentRevisionLocator={projectRevision}
            isGlobalEnabled
            isProjectEnabled
            isRevisionEnabled
          />
          <BinaryMatchDetailsModal modals={modals} projectRevision={scope.revision} />
        </>
      )}
    </div>
  );
};

export default DependenciesV2;
